import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface MilestoneAction {
  id: string;
  plan_id: string;
  milestone_index: number;
  action_index: number;
  completed: boolean;
  completed_at: string | null;
}

export const useMilestoneActions = (planId: string | null) => {
  const { user } = useAuth();
  const [actions, setActions] = useState<MilestoneAction[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchActions = useCallback(async () => {
    if (!user || !planId) return;
    setLoading(true);

    const { data, error } = await supabase
      .from('milestone_actions')
      .select('*')
      .eq('plan_id', planId);

    if (!error && data) {
      setActions(data);
    }
    setLoading(false);
  }, [user, planId]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  const isActionCompleted = useCallback(
    (milestoneIndex: number, actionIndex: number) => {
      return actions.some(
        (a) =>
          a.milestone_index === milestoneIndex &&
          a.action_index === actionIndex &&
          a.completed
      );
    },
    [actions]
  );

  const toggleAction = async (milestoneIndex: number, actionIndex: number) => {
    if (!user || !planId) return;

    const existing = actions.find(
      (a) => a.milestone_index === milestoneIndex && a.action_index === actionIndex
    );

    if (existing) {
      // Update existing
      const newCompleted = !existing.completed;
      const { error } = await supabase
        .from('milestone_actions')
        .update({
          completed: newCompleted,
          completed_at: newCompleted ? new Date().toISOString() : null,
        })
        .eq('id', existing.id);

      if (!error) {
        setActions((prev) =>
          prev.map((a) =>
            a.id === existing.id
              ? { ...a, completed: newCompleted, completed_at: newCompleted ? new Date().toISOString() : null }
              : a
          )
        );
      }
    } else {
      // Insert new
      const { data, error } = await supabase
        .from('milestone_actions')
        .insert({
          user_id: user.id,
          plan_id: planId,
          milestone_index: milestoneIndex,
          action_index: actionIndex,
          completed: true,
          completed_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (!error && data) {
        setActions((prev) => [...prev, data]);
      }
    }
  };

  const getMilestoneProgress = useCallback(
    (milestoneIndex: number, totalActions: number) => {
      const completed = actions.filter(
        (a) => a.milestone_index === milestoneIndex && a.completed
      ).length;
      return { completed, total: totalActions, percentage: totalActions > 0 ? Math.round((completed / totalActions) * 100) : 0 };
    },
    [actions]
  );

  const getOverallProgress = useCallback(
    (milestones: { actions: string[] }[]) => {
      const totalActions = milestones.reduce((sum, m) => sum + m.actions.length, 0);
      const completedActions = actions.filter((a) => a.completed).length;
      return { completed: completedActions, total: totalActions, percentage: totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0 };
    },
    [actions]
  );

  return {
    actions,
    loading,
    isActionCompleted,
    toggleAction,
    getMilestoneProgress,
    getOverallProgress,
    refetch: fetchActions,
  };
};
